/**
 * Hook for calculating responsive max points based on container width
 * Handles the responsive behavior (6-12 points) based on container dimensions
 */

import { useState, useEffect, useMemo, RefObject } from "react";
import { getResponsiveMaxPoints, ResponsivePointsConfig } from "../utils/responsivePoints";

export interface UseResponsivePointsOptions extends ResponsivePointsConfig {
  enabled?: boolean; // Whether responsive behavior is enabled (default: true)
  containerRef: RefObject<HTMLDivElement>;
}

export const useResponsivePoints = (options: UseResponsivePointsOptions): number | undefined => {
  const {
    enabled = true,
    containerRef,
    minPoints,
    maxPoints: responsiveMaxPoints,
    widthForMinPoints,
    widthForMaxPoints,
  } = options;

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  // Track container width changes with ResizeObserver
  useEffect(() => {
    if (!enabled) return;
    
    const container = containerRef.current;
    if (!container) return;

    // Set initial width
    setContainerWidth(container.offsetWidth);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setContainerWidth(width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [enabled, containerRef]);

  // Calculate responsive max points based on container width
  const calculatedMaxPoints = useMemo(() => {
    if (!enabled || containerWidth === null) {
      return undefined;
    }

    return getResponsiveMaxPoints(containerWidth, {
      minPoints,
      maxPoints: responsiveMaxPoints,
      widthForMinPoints,
      widthForMaxPoints,
    });
  }, [enabled, containerWidth, minPoints, responsiveMaxPoints, widthForMinPoints, widthForMaxPoints]);

  return calculatedMaxPoints;
};
